import { useState } from 'react';
import logo from '@/assets/images/logo.png';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as z from 'zod';

import { initiatePasswordReset } from '@/hooks/auth';

import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form';
import { Input } from '../components/ui/input';

// Zod form to validate the email form
const formSchema = z.object({
  email: z
    .string({
      required_error: 'email cannot be empty.',
    })
    .trim()
    .email()
    .transform((val) => val.toLowerCase()),
});

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  async function onSubmit(values) {
    setIsLoading(true);
    setFormMessage('');
    const { email } = values;

    await toast.promise(initiatePasswordReset(email), {
      loading: 'Sending reset instructions...',
      success: (response) => {
        setIsSuccess(true);
        return response.message || 'Reset instructions sent successfully!';
      },
      error: (error) => {
        const message =
          error.response?.data?.message || 'Failed to send reset instructions';
        setFormMessage(message);
        return message;
      },
    });

    setIsLoading(false);
  }

  if (isSuccess) {
    return (
      <div className='container relative flex h-screen items-center'>
        <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
          <div className='flex h-16 shrink-0 items-center justify-center'>
            <img className='h-20 w-auto' src={logo} alt='Your Company' />
          </div>
          <div className='flex flex-col space-y-2 text-center'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              Check your email
            </h1>
            <p className='text-sm text-muted-foreground'>
              If the email address exists in our system, you will receive
              password reset instructions shortly.
            </p>
          </div>
          <div className='text-center'>
            <Link to='/login' className='text-sm text-primary hover:underline'>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container relative flex h-screen items-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]'>
        <div className='flex h-16 shrink-0 items-center justify-center'>
          <img className='h-20 w-auto' src={logo} alt='Your Company' />
        </div>
        <div className='flex flex-col space-y-2 text-center'>
          <h1 className='text-2xl font-semibold tracking-tight'>
            Forgot Password
          </h1>
          <p className='text-sm text-muted-foreground'>
            Enter your email address and we&apos;ll send you reset instructions.
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='grid gap-3'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Email'
                        type='email'
                        autoCorrect='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button disabled={isLoading} className='mt-2'>
                {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Send Reset Instructions
              </Button>
            </div>
            {formMessage && (
              <div className='my-3'>
                <Alert
                  variant='destructive'
                  className='cursor-default select-none py-2'
                >
                  <AlertDescription className='flex items-center justify-center'>
                    {formMessage}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </form>
        </Form>
        <div className='text-center'>
          <Link to='/login' className='text-sm text-primary hover:underline'>
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
